import React from 'react';
import { Activity, Info, Layers } from 'lucide-react';
import { ScoreGauge } from './ScoreGauge';
import { RiskScale } from './RiskScale';
import { formatMessagesAnalyzed, getPatternSentence, type RiskLevel } from '../../../utils/scanReport';
interface ForensicResultHeaderProps {
  score: number;
  riskLevel: RiskLevel;
  patternCount: number;
  messageCount: number | null;
  title?: string | null;
  createdAt?: string;
  /** AI summary — only passed for full-access reports. */
  summary?: string | null;
}
const RISK_STYLES: Record<RiskLevel, {
  label: string;
  text: string;
  chip: string;
  stroke: string;
  dot: string;
  meaning: string;
}> = {
  high: {
    label: 'High Risk',
    text: 'text-forensic-crimson-soft',
    chip: 'border-forensic-crimson/40 bg-forensic-crimson/10',
    stroke: 'stroke-forensic-crimson',
    dot: 'bg-forensic-crimson',
    meaning: 'Concerning patterns appear often. Review the evidence carefully and trust what you notice.'
  },
  medium: {
    label: 'Medium Risk',
    text: 'text-amber-300',
    chip: 'border-amber-400/30 bg-amber-400/[0.08]',
    stroke: 'stroke-amber-400',
    dot: 'bg-amber-400',
    meaning: 'Some concerning patterns appear. They’re worth paying attention to over time.'
  },
  low: {
    label: 'Low Risk',
    text: 'text-emerald-300',
    chip: 'border-emerald-400/30 bg-emerald-400/[0.08]',
    stroke: 'stroke-emerald-400',
    dot: 'bg-emerald-400',
    meaning: 'Few concerning signals. This conversation looks mostly healthy.'
  }
};
export function ForensicResultHeader({
  score,
  riskLevel,
  patternCount,
  messageCount,
  title,
  createdAt,
  summary
}: ForensicResultHeaderProps) {
  const risk = RISK_STYLES[riskLevel];
  return <section aria-labelledby="result-heading" className="rounded-2xl border border-forensic-line bg-forensic-surface" data-id="element-210" data-mp="28ttke7tf7hqz">
      <div className="grid gap-8 p-6 sm:p-10 md:grid-cols-[auto_1fr] md:items-center md:gap-12" data-id="element-211" data-mp="3paxqglpakd7j">
        {/* Score — the hero */}
        <div className="flex flex-col items-center md:items-start" data-id="element-212" data-mp="3sazank3eldx2">
          <p className="self-stretch text-center font-mono text-[11px] uppercase tracking-[0.18em] text-forensic-subtle md:text-left" data-id="element-213" data-mp="3scsj94xku87p">
            Toxicity Score
          </p>
          <div className="mt-5" data-id="element-214" data-mp="569o1dqkszi1">
            <ScoreGauge score={score} strokeClass={risk.stroke} data-id="element-215" data-mp="a4tb5hgwohbz" />
          </div>
          <span className={`mt-5 inline-flex items-center gap-2 self-center rounded-full border px-3 py-1 text-xs font-semibold tracking-[0.12em] ${risk.chip} ${risk.text}`} data-id="element-216" data-mp="a2qmp1veiw9d">
            <span className={`h-1.5 w-1.5 rounded-full ${risk.dot}`} aria-hidden="true" data-id="element-217" data-mp="3djh041tbcbl3" />
            {risk.label.toUpperCase()}
          </span>
        </div>

        {/* Narrative */}
        <div className="min-w-0 border-t border-forensic-line pt-8 md:border-l md:border-t-0 md:pl-12 md:pt-0" data-id="element-218" data-mp="3nco0t9d3z3eu">
          <p className="truncate text-xs text-forensic-subtle" data-id="element-219" data-mp="2ptuyz8tdrmag">
            {title ? title : 'Case file'}
            {createdAt && <>
                {' · '}
                {new Date(createdAt).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
              </>}
          </p>
          <h1 id="result-heading" className="mt-2 text-2xl font-semibold tracking-tight text-white sm:text-[28px]" data-id="element-220" data-mp="2df6cipuoxvq1">
            Communication pattern analysis
          </h1>
          <p className="mt-3 text-base leading-relaxed text-gray-200" data-id="element-221" data-mp="1zlykix2p6y7t">
            {getPatternSentence(patternCount)}
          </p>
          <p className="mt-2 text-sm leading-relaxed text-forensic-muted" data-id="element-222" data-mp="1unx5qocb1l25">
            <span className={`font-medium ${risk.text}`} data-id="element-223" data-mp="1po0woaor33fq">What this means: </span>
            {risk.meaning}
          </p>
          {summary && <p className="mt-4 border-l-2 border-forensic-line-strong pl-4 text-sm leading-relaxed text-gray-300" data-id="element-224" data-mp="1ko6b8mnqv622">
              {summary}
            </p>}

          <div className="mt-6" data-id="element-225" data-mp="25dgx1n6dndgy">
            <RiskScale score={score} riskLevel={riskLevel} data-id="element-226" data-mp="25eik6q0o3qf1" />
          </div>

          <ul className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-forensic-line pt-5 text-xs text-forensic-muted" data-id="element-227" data-mp="2abs8u3g8fy1t">
            <li className="inline-flex items-center gap-1.5" data-id="element-228" data-mp="1wjc7r86w6a3j">
              <Activity size={13} className="text-forensic-subtle" aria-hidden="true" data-id="element-229" data-mp="20lpf211aeg2f" />
              {formatMessagesAnalyzed(messageCount)}
            </li>
            <li className="inline-flex items-center gap-1.5" data-id="element-230" data-mp="1rm2cvd0qh68b">
              <Layers size={13} className="text-forensic-subtle" aria-hidden="true" data-id="element-231" data-mp="1rnn9d86lyz8t" />
              {patternCount} {patternCount === 1 ? 'pattern' : 'patterns'} detected
            </li>
            <li className="inline-flex items-center gap-1.5" data-id="element-232" data-mp="1hoo7q9k9g3vb">
              <Info size={13} className="text-forensic-subtle" aria-hidden="true" data-id="element-233" data-mp="3802c0lrr8rui" />
              Based on communication patterns, not intent
            </li>
          </ul>
        </div>
      </div>
    </section>;
}